'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { BunnyBgVideo } from '@/components/Video/BunnyBgVideo'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { renderHeading } from '@/utilities/renderHeading'

interface Slide {
  quote: string
  authorName: string
  authorRole: string | null
}

// Figma renders the Testimonials heading as a single paragraph with three Merriweather
// runs — roman "Word ", italic "Around", roman " Town" (4022:80246 desktop /
// 4022:104259 tablet / 4022:104286 mobile). The CMS `heading` field is plain text, so a
// segment wrapped in asterisks is rendered italic, matching the mechanism TrustIndicators
// already uses. Headings without asterisks are unaffected.

export function TestimonialCarouselClient({
  slides,
  embedded,
  heading,
  backgroundUrl,
  backgroundMimeType,
  bunnyVideoId,
}: {
  slides: Slide[]
  embedded?: boolean
  heading?: string | null
  backgroundUrl?: string | null
  backgroundMimeType?: string | null
  bunnyVideoId?: string | null
}) {
  const [current, setCurrent] = useState(0)
  const [visible, setVisible] = useState(true)
  const [prefersReduced, setPrefersReduced] = useState(false)
  const isTransitioning = useRef(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const bgVideoRef = useRef<HTMLVideoElement | null>(null)
  // Defer video buffering until the band is first visible (PageSpeed 2026-08-18)
  const [videoActive, setVideoActive] = useState(false)

  // The band usually mounts below the fold, where the browser may decline the
  // `autoplay` attribute — drive play/pause from visibility instead (and stay
  // paused entirely under prefers-reduced-motion).
  useEffect(() => {
    const video = bgVideoRef.current
    if (!video) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVideoActive(true)
          video.play().catch(() => {})
        } else video.pause()
      },
      { threshold: 0.1 },
    )
    observer.observe(video)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setPrefersReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  }, [])

  const goTo = useCallback(
    (index: number) => {
      // Reduced motion: swap instantly, no fade and no autoplay (mirrors CountUpStats,
      // which already honours the preference).
      if (prefersReduced) {
        setCurrent(index)
        return
      }
      if (isTransitioning.current) return
      isTransitioning.current = true
      setVisible(false)
      setTimeout(() => {
        setCurrent(index)
        setVisible(true)
        isTransitioning.current = false
      }, 300)
    },
    [prefersReduced],
  )

  const next = useCallback(() => {
    goTo((current + 1) % slides.length)
  }, [current, slides.length, goTo])

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length)
  }, [current, slides.length, goTo])

  // Auto-play every 6 seconds. Figma states no auto-advance on 4022:80243 / 4022:104256 /
  // 4022:104283 (the chevrons are the only stated control), so this is a code-level
  // addition — it is at least suppressed for prefers-reduced-motion.
  useEffect(() => {
    if (prefersReduced) return
    intervalRef.current = setInterval(next, 6000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [next, prefersReduced])

  const slide = slides[current]

  // Figma icon button (2198:34803): type=primary, size=Regular — h36, radius sm 2px,
  // theme/accent fill, 16px Lucide chevron, hover/active → theme/secondary, 3px
  // theme/ring on focus, opacity-50 when disabled. All of that lives in the canonical
  // Button (`variant="default" size="icon"`), so reuse it rather than restyling.
  const arrows = slides.length > 1 && (
    <div className="flex items-start gap-1">
      <Button type="button" size="icon" aria-label="Previous testimonial" onClick={prev}>
        <ChevronLeft />
      </Button>
      <Button type="button" size="icon" aria-label="Next testimonial" onClick={next}>
        <ChevronRight />
      </Button>
    </div>
  )

  // Quote + author — Headings/headlineSm (24/22/20) over Body/bodySmAlt (14/16, weight 400).
  const quoteGroup = (
    <div
      className="flex flex-col gap-4 transition-opacity duration-500 ease-in-out motion-reduce:transition-none"
      style={{ opacity: visible ? 1 : 0 }}
    >
      <p className="font-display text-display-lg font-normal">&ldquo;{slide.quote}&rdquo;</p>
      <p className="font-base text-body-sm font-normal">
        {slide.authorName}
        {slide.authorRole && <>, {slide.authorRole}</>}
      </p>
    </div>
  )

  // --- Embedded (inside a Content block) ---------------------------------------
  // Figma defines a single Testimonials presentation (88:7431). Embedded reuses the
  // same type ramp and chevron controls, minus the full-bleed dark band, because it
  // sits on the light Content surface.
  if (embedded) {
    return (
      <div
        className="flex flex-col gap-6 py-[var(--spacing-section-xs)] text-foreground"
        data-visual="testimonial-carousel"
      >
        {quoteGroup}
        {arrows}
      </div>
    )
  }

  // --- Figma Testimonials band (88:7431) ---------------------------------------
  // Desktop 4022:80243 · Tablet 4022:104256 · Mobile 4022:104283.
  // Figma specifies a full-bleed background VIDEO under a dark overlay. `backgroundMedia`
  // / `bunnyVideoId` on the block supply it; bg-foreground remains the base so an unset
  // block still renders the solid dark band rather than a transparent hole.
  const isVideoUpload = Boolean(backgroundMimeType && backgroundMimeType.startsWith('video/'))

  return (
    // Asana 002 (2026-08-18, supersedes the 08-14 1600px cap): the band + its
    // background video run the FULL browser width; the content inside stays
    // capped at 1600px (the max-w on the inner flex child below) with the
    // 64/32/16 side margins supplied by px --spacing-container.
    <section
      className="relative flex w-full justify-center overflow-hidden bg-foreground pt-[var(--spacing-section-sm)] pb-[var(--spacing-section-2xs)] text-brand-foreground min-h-[650px] md:min-h-[600px] md:pt-[var(--spacing-section-md)] md:pb-[var(--spacing-section-xs)]"
      data-visual="testimonial-carousel"
    >
      {/* Background layer — Bunny stream, uploaded video, or still image. */}
      {bunnyVideoId ? (
        <BunnyBgVideo guid={bunnyVideoId} />
      ) : backgroundUrl && isVideoUpload ? (
        <video
          ref={bgVideoRef}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          src={videoActive ? backgroundUrl : undefined}
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          aria-hidden="true"
        />
      ) : backgroundUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          src={backgroundUrl}
          alt=""
          aria-hidden="true"
          loading="lazy"
        />
      ) : null}

      {/* Dark overlay (Figma) — only needed when there is actually a background behind it. */}
      {(bunnyVideoId || backgroundUrl) && (
        <div className="pointer-events-none absolute inset-0 bg-foreground/70" aria-hidden="true" />
      )}
      {/* Mobile stacks and pins the two groups to top/bottom (justify-between inside the
          650px frame); tablet and desktop are a two-column row, gap 32 → 64. */}
      <div className="relative flex w-full max-w-[var(--container-content)] px-[var(--spacing-container)] flex-col justify-between md:flex-row md:items-start md:justify-start md:gap-8 lg:gap-16">
        {/* Left column — heading + prev/next */}
        <div className="flex min-w-0 flex-col gap-6 md:flex-1">
          {/* globals.css sets an explicit `color: theme/foreground` on bare h2, so the
              inherited theme/primary-foreground has to be restated here. */}
          {heading && (
            <h2 className="font-display text-display-3xl font-normal text-brand-foreground">
              {renderHeading(heading)}
            </h2>
          )}
          {arrows}
        </div>

        {/* Right column — quote + author */}
        <div className="flex min-w-0 flex-col justify-end md:flex-1 md:justify-start">
          {quoteGroup}
        </div>
      </div>
    </section>
  )
}
